/**
 * Workspace Atoms - State management for Workspaces
 *
 * NO LOCAL TYPES - All types come from @/isomorphic/schemas
 *
 * NOTE: All action functions have been moved to actions/workspace-actions.ts
 * This file now only contains atom definitions (state)
 */

import { atom } from "jotai";
import type { Workspace, WorkspaceMember } from "@/isomorphic/schemas";
import type { WorkspaceInvitationWithDetails } from "@/isomorphic/schemas/workspace-invitation";
import type { AtomStatus, AtomError } from "@/frontend/types/atoms";

// ============================================================================
// ATOMS
// ============================================================================

/**
 * Internal atom: API workspaces only
 * Exported for use in action functions
 */
export const _apiWorkspacesAtom = atom<Workspace[]>([]);

export const workspacesStatusAtom = atom<AtomStatus>("init");
export const currentWorkspaceAtom = atom<Workspace | null>(null);
export const currentWorkspaceStatusAtom = atom<AtomStatus>("init");
export const currentWorkspaceVersionAtom = atom<number>(0);

// Workspace members
export const workspaceMembersAtom = atom<WorkspaceMember[]>([]);
export const workspaceMembersStatusAtom = atom<AtomStatus>("init");

// Workspace invitations
export const workspaceInvitationsAtom = atom<WorkspaceInvitationWithDetails[]>([]);
export const workspaceInvitationsStatusAtom = atom<AtomStatus>("init");

// Error atoms
export const workspacesErrorAtom = atom<AtomError>(null);
export const currentWorkspaceErrorAtom = atom<AtomError>(null);
export const workspaceMembersErrorAtom = atom<AtomError>(null);
export const workspaceInvitationsErrorAtom = atom<AtomError>(null);

// ============================================================================
// DERIVED ATOMS
// ============================================================================

/**
 * Public atom: All workspaces
 */
export const workspacesAtom = atom((get) => {
  const apiWorkspaces = get(_apiWorkspacesAtom);
  return apiWorkspaces;
});

/**
 * Derived atom: Current workspace ID
 * Convenient shortcut to get just the ID without the full workspace object
 */
export const currentWorkspaceIdAtom = atom((get) => {
  const currentWorkspace = get(currentWorkspaceAtom);
  return currentWorkspace?.id ?? null;
});
